import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'motion/react'
import { Tag } from '../../constants/tags'
import { cn } from '../../utils/cn'
import {
  buttonVariants,
  getPulseTransition,
  getRandomPulseDelay,
  pulseKeyframes,
} from './filterAnimation'

export type TagFilterButtonName =
  | 'human'
  | 'geometric'
  | 'text'
  | 'crest'
  | 'floral'
  | 'bird'
  | 'animal'
  | 'shape'
  | 'religious'
  | 'pattern'

// position and size of the filter buttons around the anchor point
const positionClassNames: Record<TagFilterButtonName, string> = {
  human:
    'left-[18vw] top-[-44vh] h-[14.7em] w-[14.7em] max-sm:left-[16vw] max-sm:top-[-42vh] max-sm:h-[12.5em] max-sm:w-[12.5em]',
  geometric:
    'left-[33vw] top-[-33vh] h-[17.8em] w-[17.8em] max-sm:left-[32vw] max-sm:top-[-32vh] max-sm:h-[15em] max-sm:w-[15em]',
  text: 'left-[52vw] top-[-44vh] h-[11.4em] w-[11.4em] max-sm:left-[52vw] max-sm:top-[-42vh] max-sm:h-[9.5em] max-sm:w-[9.5em]',
  crest:
    'left-[66vw] top-[10vh] h-[11.4em] w-[11.4em] max-sm:left-[70vw] max-sm:top-[12vh] max-sm:h-[9.5em] max-sm:w-[9.5em]',
  floral:
    'left-[14vw] top-[-8vh] h-[20em] w-[20em] max-sm:left-[10vw] max-sm:top-[-6vh] max-sm:h-[17em] max-sm:w-[17em]',
  bird: 'left-[35vw] top-[1vh] h-[14.7em] w-[14.7em] max-sm:left-[36vw] max-sm:top-[10vh] max-sm:h-[12.5em] max-sm:w-[12.5em]',
  animal:
    'left-[50vw] top-[-20vh] h-[17.8em] w-[17.8em] max-sm:left-[54vw] max-sm:top-[-18vh] max-sm:h-[15em] max-sm:w-[15em]',
  shape:
    'left-[76vw] top-[-10vh] h-[14.7em] w-[14.7em] max-sm:left-[80vw] max-sm:top-[-14vh] max-sm:h-[12.5em] max-sm:w-[12.5em]',
  religious:
    'left-[50vw] top-[14vh] h-[13.4em] w-[13.4em] max-sm:left-[53vw] max-sm:top-[20vh] max-sm:h-[11.5em] max-sm:w-[11.5em]',
  pattern:
    'left-[66vw] top-[-32vh] h-[14.7em] w-[14.7em] max-sm:left-[68vw] max-sm:top-[-42vh] max-sm:h-[12.5em] max-sm:w-[12.5em]',
}

const TagFilterButton = (props: {
  name: TagFilterButtonName
  tag: Tag
  onSelect: (tag: Tag) => void
}) => {
  const { i18n } = useTranslation()
  // picked once, so a re-render doesn't restart the pulse
  const [pulseDelay] = useState(getRandomPulseDelay)
  // the pulse waits for the pop-in, so the two scales don't stack while it runs
  const [isPulsing, setIsPulsing] = useState(false)

  // the wrapper pops in following the overlay's variant, the button inside it pulses
  return (
    <motion.div
      className={cn(
        'absolute text-[length:clamp(8px,calc(100vw/125),16px)]',
        positionClassNames[props.name],
      )}
      variants={buttonVariants}
      onAnimationComplete={definition => {
        if (definition === 'visible') setIsPulsing(true)
      }}
    >
      <motion.button
        type="button"
        className="flex size-full items-center justify-center overflow-hidden rounded-[50%] border-none bg-white p-0"
        animate={isPulsing ? pulseKeyframes : undefined}
        transition={getPulseTransition(pulseDelay)}
        onClick={() => props.onSelect(props.tag)}
      >
        <img
          className="h-[115%] w-[115%] object-cover text-[14px] leading-[8em] text-white lg:text-[18px]"
          src={`/icons/filter-buttons/${props.name}-filter-${i18n.language}.png`}
          alt={`${props.name} filter button`}
        />
      </motion.button>
    </motion.div>
  )
}

export default TagFilterButton
